import { type FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import { useHomePageContext } from '../HomePageContext';
import { HpSharedFeaturedModuleItemVariant } from './HpSharedFeaturedModuleItemVariant';
import { HpSharedFeaturedModuleCarouselVariant } from './HpSharedFeaturedModuleCarouselVariant';

import { type HpSharedFeaturedModuleVariant_viewer$key } from './__generated__/HpSharedFeaturedModuleVariant_viewer.graphql';
import { type HpSharedFeaturedModuleVariant_user$key } from './__generated__/HpSharedFeaturedModuleVariant_user.graphql';

import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment HpSharedFeaturedModuleVariant_viewer on Viewer {
        ...HpSharedFeaturedModuleCarouselVariant_viewer
        onSaleVerticals {
            ...HpSharedFeaturedModuleItemVariant_onSaleVertical
        }
    }
`;

const userFragment = graphql`
    fragment HpSharedFeaturedModuleVariant_user on User {
        ...HpSharedFeaturedModuleCarouselVariant_user
    }
`;

export const HpSharedFeaturedModuleVariant: FC<{
    viewer: HpSharedFeaturedModuleVariant_viewer$key;
    user?: HpSharedFeaturedModuleVariant_user$key | null;
}> = ({ viewer: viewerRef, user: userRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const { isMobile } = useHomePageContext();

    const { onSaleVerticals } = viewer;

    if (!onSaleVerticals?.length) {
        return null;
    }

    return (
        <>
            <div
                className={classnames(dibsCss.bgBuyertertiary, dibsCss.textNoir, {
                    ['primary-viewport-width']: isMobile,
                    [dibsCss.mtLarge]: !isMobile,
                })}
            >
                <div
                    className={classnames({
                        ['primary-page-width']: isMobile,
                        [dibsCss.pyLarge]: isMobile,
                        [dibsCss.pyLarger]: !isMobile,
                        [dibsCss.pxLarge]: !isMobile,
                    })}
                >
                    {!isMobile && (
                        <div
                            className={classnames(
                                dibsCss.textCenter,
                                dibsCss.sassyFontHeaderLargeItalic
                            )}
                        >
                            <FormattedMessage
                                id="HpSharedFeaturedModuleVariant.title"
                                defaultMessage="Limited Time Savings"
                            />
                        </div>
                    )}
                    <div
                        className={classnames(dibsCss.textCenter, {
                            [dibsCss.mbMedium]: isMobile,
                            [dibsCss.sassyFontHeaderLarge]: isMobile,
                            [dibsCss.mbLarger]: !isMobile,
                            [dibsCss.sassyFontHeaderVinDiesel]: !isMobile,
                        })}
                    >
                        <FormattedMessage
                            id="HpSharedFeaturedModuleVariant.subTitle"
                            defaultMessage="Sale: Up to 40% Off"
                        />
                    </div>

                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.justifyBetween,
                            dibsCss.itemsStart,
                            dibsCss.gapSmall,
                            {
                                [dibsCss.flexWrap]: isMobile,
                            }
                        )}
                    >
                        {onSaleVerticals.map((onSaleVertical, index) => (
                            <HpSharedFeaturedModuleItemVariant
                                key={index}
                                onSaleVertical={onSaleVertical}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <HpSharedFeaturedModuleCarouselVariant
                viewer={viewer}
                user={user}
                isMobile={isMobile}
            />
        </>
    );
};
